/**
 * The one place this app talks to the API.
 *
 * Everything goes through `request`, so the token, the error shape and the
 * "your session ended" handling exist once instead of in every screen.
 */

const TOKEN_KEY = 'evalyte.admin.token';

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export const tokenStore = {
  get(): string | null {
    try {
      return localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string) {
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* Private mode: the session lasts as long as the tab, which is fine. */
    }
  },
  clear() {
    try {
      localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* Nothing to clear. */
    }
  },
};

// Set when a request comes back 401, so the app can drop to the sign-in screen
// from wherever it was rather than showing an error it cannot recover from.
let onUnauthorised: (() => void) | null = null;
export function setUnauthorisedHandler(handler: () => void) {
  onUnauthorised = handler;
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // An empty filter is an absent filter. Sending `?search=` would ask the
    // API to match the empty string.
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

export async function request<T>(
  path: string,
  { method = 'GET', body, query, signal }: RequestOptions = {},
): Promise<T> {
  const token = tokenStore.get();
  const response = await fetch(buildUrl(path, query), {
    method,
    signal,
    headers: {
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    tokenStore.clear();
    onUnauthorised?.();
    throw new ApiError(401, 'unauthorised');
  }

  if (!response.ok) {
    // FastAPI puts the reason in `detail`, sometimes as a validation array.
    // Anything else that comes back is not worth showing raw.
    let message = `${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === 'string') message = payload.detail;
      else if (Array.isArray(payload?.detail)) message = payload.detail[0]?.msg ?? message;
    } catch {
      /* No body, or not JSON. The status is all we have. */
    }
    throw new ApiError(response.status, message);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export interface AccountSummary {
  id: string;
  email: string;
  display_name: string;
  is_super_admin: boolean;
}

export const api = {
  login: (email: string, password: string) =>
    request<{ access_token: string; user: AccountSummary }>('/api/v1/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  me: () => request<AccountSummary>('/api/v1/auth/me'),
};
